import { type KeyboardEvent, useRef } from 'react';
import { cn } from 'v5/platform/dom/cn';

const STEP_PX = 16;
const SHIFT_STEP_PX = 80;

const keyTarget = (
  event: KeyboardEvent,
  { value, min, max }: { value: number; min: number; max: number }
) => {
  const step = event.shiftKey ? SHIFT_STEP_PX : STEP_PX;
  switch (event.key) {
    case 'ArrowLeft':
      return value - step;
    case 'ArrowRight':
      return value + step;
    case 'Home':
      return min;
    case 'End':
      return max;
    default:
      return null;
  }
};

export interface SplitHandleProps {
  /** Where the border stands: the width of what is before it, in px. */
  value: number;
  min: number;
  max: number;
  /** While a drag runs, on every move. */
  onResize: (value: number) => void;
  /** The user's choice: as a drag ends, on every key. */
  onCommit: (value: number) => void;
  /** Double-click. */
  onReset: () => void;
  /** What the border is; the caller says what the panes are. */
  label: string;
  hint: string;
  /** Where it stands. */
  className?: string;
}

/**
 * The border between two panes the user drags, or moves with the arrows (Shift for a longer
 * step), Home and End. A drag is told on every move but chosen once, as it ends, so a stored width
 * is written once per drag. Its grip stands in the room between the panes and takes none of
 * theirs.
 */
export function SplitHandle({
  value,
  min,
  max,
  onResize,
  onCommit,
  onReset,
  label,
  hint,
  className
}: SplitHandleProps) {
  const dragRef = useRef<{
    x: number;
    from: number;
    to: number | null;
  } | null>(null);
  const clamp = (next: number) => Math.min(Math.max(next, min), max);
  const endDrag = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag && drag.to !== null) onCommit(drag.to);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={Math.round(value)}
      aria-valuemin={Math.round(min)}
      aria-valuemax={Math.round(max)}
      aria-label={label}
      title={hint}
      tabIndex={0}
      className={cn(
        'group flex cursor-col-resize touch-none items-center justify-center outline-none select-none',
        className
      )}
      onPointerDown={event => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = { x: event.clientX, from: value, to: null };
      }}
      onPointerMove={event => {
        const drag = dragRef.current;
        if (!drag) return;
        drag.to = clamp(drag.from + event.clientX - drag.x);
        onResize(drag.to);
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
      onDoubleClick={onReset}
      onKeyDown={event => {
        const target = keyTarget(event, { value, min, max });
        if (target === null) return;
        event.preventDefault();
        // the arrows are the page's own keys otherwise
        event.stopPropagation();
        onCommit(clamp(target));
      }}
    >
      <span className="h-12 w-1 rounded-full bg-fg-subtle transition-colors duration-150 group-hover:bg-border-accent-emphasis group-focus-visible:bg-border-accent-emphasis group-active:bg-border-accent-emphasis motion-reduce:transition-none" />
    </div>
  );
}
